import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserPlus, Leaf, LogOut } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { NewPatientLeadModal } from '../components/NewPatientLeadModal';
import { MergePatientLeadModal } from '../components/MergePatientLeadModal';

// Import modular components
import { PatientLeadHeader } from '../components/new-patients/PatientLeadHeader';
import { PatientLeadFilters } from '../components/new-patients/PatientLeadFilters';
import { PatientLeadsList } from '../components/new-patients/PatientLeadsList';
import { PatientLeadDetail } from '../components/new-patients/PatientLeadDetail';
import { PatientLeadForm } from '../components/new-patients/PatientLeadForm';

interface PatientLead {
  id: string;
  first_name: string;
  last_name: string;
  phone_number: string | null;
  email: string | null;
  comments: string | null;
  mapped_patient_id: string | null;
  status: string;
  created_at: string;
  created_by: string;
  progress_percentage?: number;
}

interface ZoomPatient {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  comments: string;
  selected: boolean;
}

export function NewPatients() {
  const navigate = useNavigate();
  const { user, userRoles, signOut } = useAuth();
  const [leads, setLeads] = useState<PatientLead[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showNewLeadModal, setShowNewLeadModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilters, setStatusFilters] = useState<string[]>([]);
  const [selectedLead, setSelectedLead] = useState<PatientLead | null>(null);
  const [editingLead, setEditingLead] = useState<PatientLead | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [potentialDuplicate, setPotentialDuplicate] = useState<{
    newPatient: ZoomPatient;
    existingLead: PatientLead;
    currentIndex: number;
    totalCount: number;
  } | null>(null);
  const [patientsToImport, setPatientsToImport] = useState<ZoomPatient[]>([]);
  const [isDragActive, setIsDragActive] = useState(false);
  const [sortField, setSortField] = useState<string>('created_at');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  useEffect(() => {
    fetchLeads();
    loadUserPreferences();
  }, []);

  const fetchLeads = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const { data, error } = await supabase.rpc('get_patient_leads_with_progress', {
        p_status: null,
        p_search: searchTerm || null
      });
      
      if (error) throw error;
      
      setLeads(data || []);
    } catch (error: any) {
      console.error('Error fetching patient leads:', error);
      setError('Failed to load patient leads');
    } finally {
      setLoading(false);
    }
  };

  const loadUserPreferences = async () => {
    if (!user) return;
    
    try {
      const { data, error } = await supabase
        .from('user_preferences')
        .select('preferences')
        .eq('user_id', user.id)
        .single();
      
      if (error && error.code !== 'PGRST116') { // PGRST116 is "no rows returned"
        throw error;
      }
      
      if (data?.preferences?.patientLeadFilters) {
        setStatusFilters(data.preferences.patientLeadFilters);
      }
      
      if (data?.preferences?.patientLeadSearch) {
        setSearchTerm(data.preferences.patientLeadSearch);
      }
      
      if (data?.preferences?.patientLeadSort) {
        setSortField(data.preferences.patientLeadSort.field || 'created_at');
        setSortDirection(data.preferences.patientLeadSort.direction || 'desc');
      }
    } catch (error) {
      console.error('Error loading user preferences:', error);
    }
  };

  const saveUserPreferences = async () => {
    if (!user) return;
    
    try {
      // First get existing preferences
      const { data: existingData, error: fetchError } = await supabase
        .from('user_preferences')
        .select('preferences')
        .eq('user_id', user.id)
        .single();
      
      // Prepare new preferences object
      const newPreferences = {
        ...(existingData?.preferences || {}),
        patientLeadFilters: statusFilters,
        patientLeadSearch: searchTerm,
        patientLeadSort: {
          field: sortField,
          direction: sortDirection
        }
      };
      
      // Upsert preferences
      const { error: upsertError } = await supabase
        .from('user_preferences')
        .upsert({
          user_id: user.id,
          preferences: newPreferences
        });
      
      if (upsertError) throw upsertError;
    } catch (error) {
      console.error('Error saving user preferences:', error);
    }
  };

  // Debounced save preferences
  useEffect(() => {
    const saveTimer = setTimeout(() => {
      if (user) {
        saveUserPreferences();
      }
    }, 1000);
    
    return () => clearTimeout(saveTimer);
  }, [statusFilters, searchTerm, sortField, sortDirection, user]);

  const handleSaveLead = async (lead: Partial<PatientLead>) => {
    try {
      setIsSubmitting(true);
      setError(null);
      
      if (lead.id) {
        // Update existing lead
        const { error: updateError } = await supabase
          .from('patient_leads')
          .update({
            first_name: lead.first_name,
            last_name: lead.last_name,
            phone_number: lead.phone_number,
            email: lead.email,
            comments: lead.comments,
            status: lead.status
          })
          .eq('id', lead.id);
          
        if (updateError) throw updateError;
      } else {
        // Create new lead
        const { error: insertError } = await supabase
          .from('patient_leads')
          .insert({
            first_name: lead.first_name,
            last_name: lead.last_name,
            phone_number: lead.phone_number,
            email: lead.email,
            comments: lead.comments,
            status: lead.status || 'new',
            created_by: user?.id
          });
          
        if (insertError) throw insertError;
      }
      
      await fetchLeads();
      setShowNewLeadModal(false);
      setEditingLead(null);
    } catch (error: any) {
      console.error('Error saving patient lead:', error);
      setError(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteLead = async (id: string) => {
    try {
      setIsDeleting(true);
      setError(null);
      
      const { error } = await supabase.rpc('delete_patient_lead', {
        p_lead_id: id
      });
      
      if (error) throw error;
      
      await fetchLeads();
      if (selectedLead?.id === id) {
        setSelectedLead(null);
      }
    } catch (error: any) {
      console.error('Error deleting patient lead:', error);
      setError(error.message);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleFileDrop = (acceptedFiles: File[]) => {
    setIsDragActive(false);
    if (acceptedFiles.length === 0) return;
    
    const file = acceptedFiles[0];
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const content = e.target?.result as string;
        const lines = content.split('\n');
        
        // Skip header row
        const patientData: ZoomPatient[] = [];
        
        for (let i = 1; i < lines.length; i++) {
          const line = lines[i].trim();
          if (!line) continue;
          
          // Split by comma but handle quoted values correctly
          const columns = parseCSVLine(line);
          if (columns.length < 22) {
            console.warn(`Line ${i} has insufficient columns:`, columns.length);
            continue;
          }
          
          // Map to the correct columns based on the specified indices
          // Last name: column 5 (index 4)
          // First name: column 4 (index 3)
          // Email: column 6 (index 5)
          // Phone: column 20 (index 19)
          // Comments: column 22 (index 21)
          patientData.push({
            lastName: columns[4]?.trim() || '',
            firstName: columns[3]?.trim() || '',
            email: columns[5]?.trim() || '',
            phoneNumber: columns[19]?.trim() || '',
            comments: columns[21]?.trim() || '',
            selected: true
          });
        }
        
        if (patientData.length > 0) {
          handleImportPatients(patientData);
        } else {
          setError('No valid patient data found in the file');
        }
      } catch (err: any) {
        setError(`Error parsing file: ${err.message}`);
      }
    };
    
    reader.onerror = () => {
      setError('Error reading file');
    };
    
    reader.readAsText(file);
  };

  // Function to properly parse CSV lines with quoted values
  function parseCSVLine(line: string): string[] {
    const result: string[] = [];
    let currentValue = '';
    let insideQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      
      if (char === '"') {
        // Handle quotes
        if (i + 1 < line.length && line[i + 1] === '"') {
          // Double quotes inside quoted string - add a single quote
          currentValue += '"';
          i++; // Skip the next quote
        } else {
          // Toggle quote mode
          insideQuotes = !insideQuotes;
        }
      } else if (char === ',' && !insideQuotes) {
        // End of field
        result.push(currentValue);
        currentValue = '';
      } else {
        // Regular character
        currentValue += char;
      }
    }
    
    // Add the last field
    result.push(currentValue);
    
    return result;
  }

  const handleImportPatients = async (patients: ZoomPatient[]) => {
    setPatientsToImport(patients);
    
    // Check for potential duplicates
    const potentialDuplicates = await findPotentialDuplicates(patients);
    
    if (potentialDuplicates.length > 0) {
      // Show merge modal for the first potential duplicate
      setPotentialDuplicate({
        newPatient: potentialDuplicates[0].newPatient,
        existingLead: potentialDuplicates[0].existingLead,
        currentIndex: 1,
        totalCount: potentialDuplicates.length
      });
      setShowMergeModal(true);
    } else {
      // No duplicates, proceed with import
      await importPatients(patients);
    }
  };

  const findPotentialDuplicates = async (patients: ZoomPatient[]) => {
    // This is a simplified version - in a real app, you'd do more sophisticated matching
    const duplicates = [];
    
    for (const patient of patients) {
      const matchingLeads = leads.filter(lead => 
        (lead.first_name.toLowerCase() === patient.firstName.toLowerCase() && 
         lead.last_name.toLowerCase() === patient.lastName.toLowerCase()) ||
        (lead.email && patient.email && 
         lead.email.toLowerCase() === patient.email.toLowerCase()) ||
        (lead.phone_number && patient.phoneNumber &&
         lead.phone_number.replace(/\D/g, '') === patient.phoneNumber.replace(/\D/g, ''))
      );
      
      if (matchingLeads.length > 0) {
        duplicates.push({
          newPatient: patient,
          existingLead: matchingLeads[0]
        });
      }
    }
    
    return duplicates;
  };

  const importPatients = async (patients: ZoomPatient[]) => {
    try {
      setIsSubmitting(true);
      setError(null);
      
      for (const patient of patients) {
        if (!patient.selected) continue;
        
        await supabase
          .from('patient_leads')
          .insert({
            first_name: patient.firstName,
            last_name: patient.lastName,
            phone_number: patient.phoneNumber || null,
            email: patient.email || null,
            comments: patient.comments || null,
            status: 'new',
            created_by: user?.id
          });
      }
      
      await fetchLeads();
    } catch (error: any) {
      console.error('Error importing patients:', error);
      setError(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMergePatient = async () => {
    if (!potentialDuplicate) return;
    
    try {
      setIsSubmitting(true);
      
      // Update the existing lead with any new information
      const { existingLead, newPatient } = potentialDuplicate;
      
      // Merge comments
      let mergedComments = existingLead.comments || '';
      if (newPatient.comments) {
        mergedComments += mergedComments ? `<div class="text-gray-700 mt-3"><strong>Additional Notes (${new Date().toLocaleDateString()}):</strong><p>${newPatient.comments}</p></div>` : newPatient.comments;
      }
      
      await supabase
        .from('patient_leads')
        .update({
          phone_number: existingLead.phone_number || newPatient.phoneNumber || null,
          email: existingLead.email || newPatient.email || null,
          comments: mergedComments
        })
        .eq('id', existingLead.id);
      
      // Move to next duplicate or finish
      handleNextDuplicate();
    } catch (error: any) {
      console.error('Error merging patient:', error);
      setError(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCreateNewFromDuplicate = async () => {
    if (!potentialDuplicate) return;
    
    try {
      setIsSubmitting(true);
      
      // Create a new lead
      const { newPatient } = potentialDuplicate;
      
      await supabase
        .from('patient_leads')
        .insert({
          first_name: newPatient.firstName,
          last_name: newPatient.lastName,
          phone_number: newPatient.phoneNumber || null,
          email: newPatient.email || null,
          comments: newPatient.comments || null,
          status: 'new',
          created_by: user?.id
        });
      
      // Move to next duplicate or finish
      handleNextDuplicate();
    } catch (error: any) {
      console.error('Error creating new patient:', error);
      setError(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNextDuplicate = async () => {
    if (!potentialDuplicate) return;
    
    const { currentIndex, totalCount } = potentialDuplicate;
    
    if (currentIndex < totalCount) {
      // Find the next duplicate
      const nextDuplicates = await findPotentialDuplicates(patientsToImport.slice(currentIndex));
      
      if (nextDuplicates.length > 0) {
        setPotentialDuplicate({
          newPatient: nextDuplicates[0].newPatient,
          existingLead: nextDuplicates[0].existingLead,
          currentIndex: currentIndex + 1,
          totalCount
        });
        return;
      }
    }
    
    // No more duplicates, import remaining patients
    const remainingPatients = patientsToImport.slice(potentialDuplicate.currentIndex)
      .filter(p => p.selected);
    
    if (remainingPatients.length > 0) {
      await importPatients(remainingPatients);
    }
    
    setShowMergeModal(false);
    setPotentialDuplicate(null);
    await fetchLeads();
  };

  const handleSkipDuplicate = () => {
    handleNextDuplicate();
  };

  const handleSortChange = (field: string) => {
    if (sortField === field) {
      // Toggle direction if same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field and default direction
      setSortField(field);
      // Default to descending for dates and progress, ascending for names
      setSortDirection(field === 'name' ? 'asc' : 'desc');
    }
  };

  // Apply sorting to the filtered leads
  const sortedLeads = [...leads].sort((a, b) => {
    const direction = sortDirection === 'asc' ? 1 : -1;
    
    switch (sortField) {
      case 'name':
        const nameA = `${a.last_name}, ${a.first_name}`.toLowerCase();
        const nameB = `${b.last_name}, ${b.first_name}`.toLowerCase();
        return nameA.localeCompare(nameB) * direction;
      
      case 'created_at':
        return (new Date(a.created_at).getTime() - new Date(b.created_at).getTime()) * direction;
      
      case 'progress':
        const progressA = a.progress_percentage || 0;
        const progressB = b.progress_percentage || 0;
        return (progressA - progressB) * direction;
      
      default:
        return 0;
    }
  });

  // Filter leads based on search and status
  const filteredLeads = sortedLeads.filter(lead => {
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = 
      lead.first_name.toLowerCase().includes(searchLower) ||
      lead.last_name.toLowerCase().includes(searchLower) ||
      (lead.email && lead.email.toLowerCase().includes(searchLower)) ||
      (lead.phone_number && lead.phone_number.toLowerCase().includes(searchLower));
    
    const matchesStatus = statusFilters.length === 0 || statusFilters.includes(lead.status);
    
    return matchesSearch && matchesStatus;
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50">
      {/* Header/Navigation Bar */}
      <nav className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <Leaf className="h-6 w-6 text-green-600" />
              <div className="ml-2">
                <span className="text-xl font-semibold text-gray-900">THRIVE</span>
                <span className="ml-2 text-sm text-gray-500">New Patient Leads</span>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-700">{user?.email}</span>
              <div className="flex flex-wrap gap-2">
                {userRoles.map((role) => (
                  <span
                    key={role}
                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800"
                  >
                    {role}
                  </span>
                ))}
              </div>
              <button
                onClick={() => signOut()}
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Sign Out
              </button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-4 sm:px-0">
          {/* Header with CSV import functionality */}
          <PatientLeadHeader 
            onAddNewLead={() => setShowNewLeadModal(true)} 
            onFileDrop={handleFileDrop}
            isDragActive={isDragActive}
          />

          {/* Filters */}
          <PatientLeadFilters 
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            statusFilters={statusFilters}
            onStatusFiltersChange={setStatusFilters}
          />

          {/* Main Content */}
          <div className="bg-white shadow-lg rounded-lg overflow-hidden">
            <div className="px-6 py-5 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-lg font-medium text-gray-900">
                Patient Leads ({filteredLeads.length})
              </h3>
              <button
                onClick={() => setShowNewLeadModal(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
              >
                <UserPlus className="h-4 w-4 mr-2" />
                Add New Lead
              </button>
            </div>

            {/* Patient Leads List */}
            <PatientLeadsList 
              leads={filteredLeads}
              onEditLead={setEditingLead}
              onDeleteLead={handleDeleteLead}
              onLeadClick={setSelectedLead}
              isDeleting={isDeleting}
              sortField={sortField}
              sortDirection={sortDirection}
              onSortChange={handleSortChange}
            />
          </div>
        </div>
      </div>

      {/* New/Edit Lead Modal */}
      <PatientLeadForm
        isOpen={showNewLeadModal || !!editingLead}
        onClose={() => {
          setShowNewLeadModal(false);
          setEditingLead(null);
        }}
        onSave={handleSaveLead}
        lead={editingLead}
        isSubmitting={isSubmitting}
      />

      {/* Lead Detail Modal */}
      {selectedLead && (
        <PatientLeadDetail
          lead={selectedLead}
          onClose={() => setSelectedLead(null)}
          onEdit={(lead) => {
            setEditingLead(lead);
            setSelectedLead(null);
          }}
          onProgressUpdate={fetchLeads}
        />
      )}

      {/* Merge Modal */}
      {showMergeModal && potentialDuplicate && (
        <MergePatientLeadModal
          isOpen={showMergeModal}
          onClose={() => {
            setShowMergeModal(false);
            setPotentialDuplicate(null);
          }}
          newPatient={potentialDuplicate.newPatient}
          existingLead={potentialDuplicate.existingLead}
          onMerge={handleMergePatient}
          onCreateNew={handleCreateNewFromDuplicate}
          onSkip={handleSkipDuplicate}
          currentIndex={potentialDuplicate.currentIndex}
          totalCount={potentialDuplicate.totalCount}
        />
      )}
    </div>
  );
}